import { useEffect, useState } from "react";
import { X } from "lucide-react";
import { useLocale } from "@/lib/locale";

const DISMISSED_ON_KEY = "ovvo-turismo-natura-banner-dismissed-on";

const copyByLocale = {
  it: {
    imageAlt: "Fiera del Camper, Camping, Nautica e Vita all’aria aperta, dal 13 al 15 novembre 2026 a Montichiari.",
    close: "Chiudi l’avviso sulla fiera Turismo Natura",
    region: "Avviso sulla fiera Turismo Natura",
  },
  en: {
    imageAlt: "Camper, Camping, Boating and Outdoor Living Fair, November 13–15, 2026, in Montichiari, Italy.",
    close: "Close the Turismo Natura fair announcement",
    region: "Turismo Natura fair announcement",
  },
  de: {
    imageAlt: "Messe für Wohnmobile, Camping, Boote und Outdoor-Leben, vom 13. bis 15. November 2026 in Montichiari, Italien.",
    close: "Hinweis zur Messe Turismo Natura schließen",
    region: "Hinweis zur Messe Turismo Natura",
  },
  hr: {
    imageAlt: "Sajam kampera, kampiranja, nautike i boravka na otvorenom, od 13. do 15. studenoga 2026. u Montichiariju u Italiji.",
    close: "Zatvori obavijest o sajmu Turismo Natura",
    region: "Obavijest o sajmu Turismo Natura",
  },
} as const;

function getLocalDateKey() {
  const today = new Date();
  const month = String(today.getMonth() + 1).padStart(2, "0");
  const day = String(today.getDate()).padStart(2, "0");
  return `${today.getFullYear()}-${month}-${day}`;
}

function shouldShowBanner() {
  try {
    return localStorage.getItem(DISMISSED_ON_KEY) !== getLocalDateKey();
  } catch {
    return true;
  }
}

export function EventBanner() {
  const { locale } = useLocale();
  const [isVisible, setIsVisible] = useState<boolean | null>(null);

  useEffect(() => {
    const updateVisibility = () => setIsVisible(shouldShowBanner());
    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") updateVisibility();
    };

    updateVisibility();
    window.addEventListener("storage", updateVisibility);
    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      window.removeEventListener("storage", updateVisibility);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, []);

  const dismiss = () => {
    setIsVisible(false);
    try {
      localStorage.setItem(DISMISSED_ON_KEY, getLocalDateKey());
    } catch {
      // Keep the banner closed for this page view if browser storage is unavailable.
    }
  };

  if (!isVisible) return null;

  const copy = copyByLocale[locale];

  return (
    <aside
      aria-label={copy.region}
      className="fixed right-3 top-20 z-[60] w-[min(12rem,calc(100vw-1.5rem))] sm:right-4 sm:top-[92px] sm:w-80"
    >
      <div className="relative overflow-hidden rounded-xl border border-white/80 bg-white shadow-2xl">
        <img
          src={`${import.meta.env.BASE_URL}turismo-natura-2026.jpeg`}
          alt={copy.imageAlt}
          className="block h-auto w-full"
          decoding="async"
        />
        <button
          type="button"
          onClick={dismiss}
          aria-label={copy.close}
          className="absolute right-2 top-2 flex h-10 w-10 items-center justify-center rounded-full bg-white/95 text-slate-900 shadow-md transition hover:bg-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2"
        >
          <X aria-hidden="true" className="h-5 w-5" />
        </button>
      </div>
    </aside>
  );
}
